import React from 'react';
import styled from 'styled-components';
import { DiscontinousSelection } from '../../lib/discontinous-selection/components/discontinous-selection.component';
import { TextSegment } from '../../text-highlighter/components/text-segment.component';
import { HighlightCollection } from '../../text-highlighter/models/highlight-collection.model';
import { UserHighlight } from '../../text-highlighter/models/user-highlight.model';
import { PDF_SELECTION_MODE } from '../models/pdf-selection-modes.model';
import { Annotation } from '../../annotations/models/annotation.model';
import AnnotationPopover from '../../annotations/components/annotation-popover.component';
import { PDFTextContent, PDFTextItem } from '../models/pdf-text-content.model';
import { PDFJSPageViewport } from '../models/pdfjs-page-viewport.model';

const StyledContent = styled.div`
  position: absolute;
  text-align: left;
  color: transparent;

  .oseberg-text-highlight {
    &::selection {
      color: rgba(0, 0, 0, 0);
      background: rgba(50, 151, 253, 0.4);
    }
  }
`;

export interface PDFAnnotationLayerProps {
  highlights: HighlightCollection;
  textOffsets: number[];
  pdfTextContent: PDFTextContent;
  viewport: PDFJSPageViewport;
  onSelectionChange: (elm: HTMLElement[]) => any;
  selectionMode: PDF_SELECTION_MODE;
  editable?: boolean;
  annotations?: Map<string, Annotation>;
  annotationTypes?: Map<string, string>;
  selectedAnnotations?: string[];
  onAnnotationClick?: (annotationId: string, e: React.MouseEvent) => void;
  onSelectAnnotations?: (annotationIds: string[]) => void;
  onSelectAnnotationsOfType?: (annotationType: string) => void;
  onClearAnnotationSelection?: () => void;
  onFocusAnnotation?: (annotationId: string) => void;
  relationshipViewActive?: boolean;
  onRelationshipViewClick?: (annotationType: string | null) => void;
}
export class PDFAnnotationLayer extends React.Component<
  PDFAnnotationLayerProps
> {
  static className = 'ose-pdf-annotation-layer';

  shouldComponentUpdate(nextProps: PDFAnnotationLayerProps) {
    const shouldUpdate =
      this.props.highlights.id !== nextProps.highlights.id ||
      this.props.viewport !== nextProps.viewport ||
      this.props.pdfTextContent !== nextProps.pdfTextContent ||
      this.props.selectionMode !== nextProps.selectionMode ||
      this.props.selectedAnnotations !== nextProps.selectedAnnotations ||
      this.props.relationshipViewActive !== nextProps.relationshipViewActive;

    return shouldUpdate;
  }

  get scale() {
    return this.props.viewport.scale;
  }

  scaleByTransform(scaleBy: number) {
    return [scaleBy, 0, 0, scaleBy, 0, 0];
  }

  /**
   * Returns the highlighted text segments for a text item. Or, if the text item
   * is not highlighted, only the text as the element content.
   */
  renderHighlightContent(
    textItem: PDFTextItem,
    textOffset: number,
  ): JSX.Element[] | string {
    const str = textItem.str.trim();
    const endOffset = textOffset + str.length;
    const highlights = this.props.highlights.overlapsRange(
      textOffset,
      endOffset,
    );

    return highlights.length
      ? createHighlightElements(
          textItem,
          highlights,
          textOffset,
          this.props.editable ? undefined : renderWithPopover,
        )
      : textItem.str;
  }

  /**
   * Inverts Y coordinate system and scales to vieport.
   * PDF y coordinates begin from bottom left, but screen
   * coordinates begin from top-left. This inverts pdf y coordinates
   * to match the screen coordinate system.
   * @param transform
   * @param viewport
   */
  pdfToScreen(transform: number[], viewport: PDFJSPageViewport): number[] {
    const yMax = this.props.viewport.viewBox[3];
    const scaleY = this.vectorLength(transform[2], transform[3]);
    return [
      transform[0],
      -transform[1],
      -transform[2],
      transform[3],
      transform[4] * viewport.scale,
      (yMax - transform[5] - scaleY) * viewport.scale,
    ];
  }

  transform(m1: number[], m2: number[]): number[] {
    return [
      m1[0] * m2[0] + m1[2] * m2[1],
      m1[1] * m2[0] + m1[3] * m2[1],
      m1[0] * m2[2] + m1[2] * m2[3],
      m1[1] * m2[2] + m1[3] * m2[3],
      m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
      m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
    ];
  }

  vectorLength(x: number, y: number): number {
    return Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));
  }

  extractScale(transform: number[]) {
    return [
      this.vectorLength(transform[0], transform[1]),
      this.vectorLength(transform[2], transform[3]),
    ];
  }

  /**
   * Returns the font size of a transform by measuring the
   * length of the y
   * @param transform
   */
  fontSize(transform: number[]) {
    return this.vectorLength(transform[2], transform[3]);
  }

  renderTextItem = (item: PDFTextItem, idx: number) => {
    const { textOffsets } = this.props;
    const scale = this.scale;
    const fontSize = this.fontSize(item.transform);

    const txItem = this.pdfToScreen(item.transform, this.props.viewport);
    const txReverseFontScaling = this.scaleByTransform(1 / fontSize);

    // Transform includes font size, remove font-size adjustment from transform.
    // (best allow css handle that).
    const tx2 = this.transform(txItem, txReverseFontScaling);

    const style = {
      position: 'absolute' as const,
      fontFamily: 'Courier',
      transformOrigin: 'left bottom 0px',
      height: '1em',
      whiteSpace: 'pre',
      fontSize: `${fontSize * scale}px`,
      color: 'rbga(255, 0, 0, 1)',
      transform: `matrix(${tx2.join(',')})`,
    };

    const startOffset = textOffsets[idx];

    return (
      <TextSegment
        key={idx}
        style={style}
        startOffset={startOffset}
        endOffset={
          typeof startOffset === 'number'
            ? textOffsets[idx] + item.str.trim().length
            : undefined
        }
      >
        {this.renderHighlightContent(item, textOffsets[idx])}
      </TextSegment>
    );
  };

  render() {
    const { pdfTextContent, viewport, selectionMode } = this.props;
    const width = viewport.viewBox[2] * this.scale;
    const height = viewport.viewBox[3] * this.scale;

    const style = {
      width,
      height,
      // These styles move this element off the canvas and back on using a transform.
      // This fixes severe performance issues with paint calls when the child text segements
      // use a matrix transform. Not absolutely certain why it works, but it's possible it causes
      // the whole element to be rendered off-screen, and then painted as a whole in position.
      left: `${viewport.width / 2}px`,
      top: `${viewport.height / 2}px`,
      transform: `translate(-${width / 2}px, -${height / 2}px) rotate(${
        viewport.rotation
      }deg)`,
    };

    const annotations = this.props.annotations;
    const layerRef = { current: null as HTMLElement | null };
    const dragState = { active: false, startX: 0, startY: 0 };

    const getAnnotationIdsFromElement = (
      target: HTMLElement,
      container: HTMLElement,
    ): string[] => {
      const ids: string[] = [];
      let el: HTMLElement | null = target;
      while (el && el !== container) {
        Array.from(el.classList).forEach(c => {
          if (c.startsWith('annotation-')) {
            const raw = c.replace('annotation-', '');
            const stripped = raw.replace(/-\d+$/, '');
            const id =
              stripped && annotations && annotations.has(stripped)
                ? stripped
                : raw;
            if (id && ids.indexOf(id) < 0) {
              ids.push(id);
            }
          }
        });
        el = el.parentElement;
      }
      return ids;
    };

    const getShortestAnnotation = (ids: string[]): string | null => {
      if (ids.length === 0) return null;
      let shortestId = ids[0];
      let shortestLen = Infinity;
      for (const id of ids) {
        const ann = annotations?.get(id);
        if (ann && ann.start !== undefined && ann.end !== undefined) {
          const len = ann.end - ann.start;
          if (len < shortestLen) {
            shortestLen = len;
            shortestId = id;
          }
        }
      }
      return shortestId;
    };

    const sortByOffset = (ids: string[]): string[] => {
      if (!annotations) return ids;
      return ids.slice().sort((a, b) => {
        const aStart = annotations.get(a)?.start ?? Infinity;
        const bStart = annotations.get(b)?.start ?? Infinity;
        return aStart - bStart;
      });
    };

    const getAnnotationIdsInRect = (
      container: HTMLElement,
      rect: { x1: number; y1: number; x2: number; y2: number },
    ): string[] => {
      const elements = container.querySelectorAll('.oseberg-text-highlight');
      const found = new Set<string>();
      const containerRect = container.getBoundingClientRect();
      elements.forEach(el => {
        const elRect = el.getBoundingClientRect();
        const relLeft = elRect.left - containerRect.left;
        const relTop = elRect.top - containerRect.top;
        const relRight = relLeft + elRect.width;
        const relBottom = relTop + elRect.height;
        if (
          relRight > rect.x1 &&
          relLeft < rect.x2 &&
          relBottom > rect.y1 &&
          relTop < rect.y2
        ) {
          Array.from(el.classList).forEach(c => {
            if (c.startsWith('annotation-')) {
              const raw = c.replace('annotation-', '');
              const stripped = raw.replace(/-\d+$/, '');
              const id =
                stripped && annotations && annotations.has(stripped)
                  ? stripped
                  : raw;
              if (id) found.add(id);
            }
          });
        }
      });
      return sortByOffset(Array.from(found));
    };

    const handleMouseDown = (e: React.MouseEvent) => {
      // In relationship view mode without modifiers, prevent text selection and D3
      if (this.props.relationshipViewActive && !(e.metaKey || e.ctrlKey)) {
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
        return;
      }
      if (!(e.metaKey || e.ctrlKey)) return;

      const clickedIds = getAnnotationIdsFromElement(
        e.target as HTMLElement,
        e.currentTarget as HTMLElement,
      );

      // Ctrl+Alt+Click: select all annotations of the same type on the page
      if (e.altKey && clickedIds.length > 0) {
        e.stopPropagation();
        e.preventDefault();
        const clickedId = getShortestAnnotation(clickedIds);
        const types = this.props.annotationTypes;
        if (!clickedId || !types) return;
        const clickedType = types.get(clickedId);
        if (!clickedType) return;
        this.props.onSelectAnnotationsOfType?.(clickedType);
        return;
      }

      if (clickedIds.length > 0) {
        e.stopPropagation();
        e.preventDefault();
        const shortestId = getShortestAnnotation(clickedIds);
        if (shortestId) {
          this.props.onAnnotationClick?.(shortestId, e);
        }
        return;
      }

      // No annotation clicked — start drag selection
      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      window.getSelection()?.removeAllRanges();
      const container = e.currentTarget as HTMLElement;
      const containerRect = container.getBoundingClientRect();
      dragState.active = true;
      dragState.startX = e.clientX - containerRect.left;
      dragState.startY = e.clientY - containerRect.top;
      layerRef.current = container;
      container.style.userSelect = 'none';
      (container.style as any).webkitUserSelect = 'none';

      // Create visual selection rectangle
      const selBox = document.createElement('div');
      selBox.style.position = 'absolute';
      selBox.style.border = '2px dashed #1890ff';
      selBox.style.borderRadius = '4px';
      selBox.style.backgroundColor = 'rgba(24, 144, 255, 0.08)';
      selBox.style.pointerEvents = 'none';
      selBox.style.zIndex = '9999';
      container.appendChild(selBox);

      // Capture existing selection to merge with drag results
      const priorSelection = this.props.selectedAnnotations || [];

      let lastSelRect = { x1: 0, y1: 0, x2: 0, y2: 0 };

      const onMouseMove = (ev: MouseEvent) => {
        if (!dragState.active) return;
        ev.preventDefault();
        const rect = layerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const curX = ev.clientX - rect.left;
        const curY = ev.clientY - rect.top;
        lastSelRect = {
          x1: Math.min(dragState.startX, curX),
          y1: Math.min(dragState.startY, curY),
          x2: Math.max(dragState.startX, curX),
          y2: Math.max(dragState.startY, curY),
        };

        selBox.style.left = lastSelRect.x1 + 'px';
        selBox.style.top = lastSelRect.y1 + 'px';
        selBox.style.width = lastSelRect.x2 - lastSelRect.x1 + 'px';
        selBox.style.height = lastSelRect.y2 - lastSelRect.y1 + 'px';
      };

      const preventSelect = (ev: Event) => ev.preventDefault();

      const onMouseUp = () => {
        dragState.active = false;
        const dragIds = getAnnotationIdsInRect(layerRef.current!, lastSelRect);
        const merged = Array.from(new Set([...priorSelection, ...dragIds]));
        if (merged.length > 0) {
          this.props.onSelectAnnotations?.(merged);
        }
        selBox.remove();
        if (layerRef.current) {
          layerRef.current.style.userSelect = '';
          (layerRef.current.style as any).webkitUserSelect = '';
        }
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.removeEventListener('selectstart', preventSelect);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.addEventListener('selectstart', preventSelect);
    };

    const handleClick = (e: React.MouseEvent) => {
      if (this.props.relationshipViewActive && !(e.metaKey || e.ctrlKey)) {
        // In relationship view without modifiers, click annotation to focus type
        this.props.onClearAnnotationSelection?.();
        const clickedIds = getAnnotationIdsFromElement(
          e.target as HTMLElement,
          e.currentTarget as HTMLElement,
        );
        if (clickedIds.length > 0) {
          const clickedId = getShortestAnnotation(clickedIds);
          const types = this.props.annotationTypes;
          if (clickedId && types) {
            const clickedType = types.get(clickedId);
            this.props.onRelationshipViewClick?.(clickedType || null);
            this.props.onFocusAnnotation?.(clickedId);
          }
        } else {
          this.props.onRelationshipViewClick?.(null);
        }
        return;
      }
      if (!(e.metaKey || e.ctrlKey)) {
        this.props.onClearAnnotationSelection?.();
      }
    };

    const contentStyle = this.props.relationshipViewActive
      ? {
          ...style,
          userSelect: 'none' as const,
          WebkitUserSelect: 'none' as const,
        }
      : style;

    return (
      <StyledContent
        className={PDFAnnotationLayer.className}
        style={contentStyle}
        onMouseDownCapture={handleMouseDown}
        onClick={handleClick}
      >
        {selectionMode === 'selection-box' &&
          !this.props.relationshipViewActive && (
            <DiscontinousSelection
              onSelectionChange={this.props.onSelectionChange}
              rotatedBy={viewport.rotation}
            >
              {pdfTextContent.items.map(this.renderTextItem)}
            </DiscontinousSelection>
          )}
        {(selectionMode === 'normal' ||
          (selectionMode === 'selection-box' &&
            this.props.relationshipViewActive)) &&
          pdfTextContent.items.map(this.renderTextItem)}
      </StyledContent>
    );
  }
}

/**
 * Renders text highlights for a pdf.js text item.
 *
 * Accepts a pdf.js PDFTextItem, the highlights present in a document,
 * and the starting character offset of this text document.
 * Returns the text segments with proper highlights.
 * @param textItem
 * @param highlights
 * @param startOffset
 */
type RenderFn = (el: JSX.Element, highlights: UserHighlight[]) => JSX.Element;

function createHighlightElements(
  textItem: PDFTextItem,
  highlights: UserHighlight[],
  startOffset: number,
  render?: RenderFn,
): JSX.Element[] {
  const endOffset = startOffset + textItem.str.trim().length;
  const elements: any[] = [];

  if (startOffset < highlights[0].start) {
    const highlight = highlights[0];
    elements.push(
      createNonhighlightedElement(
        textItem,
        startOffset,
        highlight.start,
        startOffset,
      ),
    );
  }

  elements.push(
    createHighlightElement(textItem, highlights[0], startOffset, render),
  );

  for (let i = 1; i < highlights.length; i++) {
    const highlight = highlights[i];
    const lastHighlight = highlights[i - 1];
    if (highlight.start !== lastHighlight.end) {
      elements.push(
        createNonhighlightedElement(
          textItem,
          lastHighlight.end,
          highlight.start,
          startOffset,
        ),
      );
    }

    elements.push(
      createHighlightElement(textItem, highlight, startOffset, render),
    );
  }

  if (endOffset > highlights[highlights.length - 1].end) {
    const highlight = highlights[highlights.length - 1];

    elements.push(
      createNonhighlightedElement(
        textItem,
        highlight.end,
        endOffset,
        startOffset,
      ),
    );
  }

  return elements;
}

function createNonhighlightedElement(
  text: PDFTextItem,
  start: number,
  end: number,
  startOffset: number,
): JSX.Element {
  const overlayText = text.str
    .trim()
    .slice(Math.max(start - startOffset, 0), end - startOffset);
  const endOffset = Math.min(end, startOffset + overlayText.length);
  return (
    <TextSegment
      key={`${overlayText}_${startOffset}_${endOffset}`}
      className="pdf-text-content"
      startOffset={start}
      endOffset={endOffset}
      style={{ color: 'rgba(0, 0, 0, 0)' }}
    >
      {overlayText}
    </TextSegment>
  );
}

function createHighlightElement(
  text: PDFTextItem,
  highlight: UserHighlight,
  startOffset: number,
  render?: RenderFn,
): JSX.Element {
  const textSlice = text.str
    .trim()
    .slice(
      Math.max(highlight.start - startOffset, 0),
      highlight.end - startOffset,
    );

  return (
    <TextSegment
      key={highlight.id}
      className="pdf-text-content"
      startOffset={Math.max(highlight.start, startOffset)}
      endOffset={Math.min(highlight.end, startOffset + text.str.length)}
      highlight={highlight}
      render={render}
      style={{
        color: 'rgba(0, 0, 0, 0)',
        backgroundColor: `rgba(${highlight.color.r}, ${highlight.color.g}, ${highlight.color.b}, 0.35)`,
      }}
    >
      {textSlice}
    </TextSegment>
  );
}

function renderWithPopover(
  RenderElement: JSX.Element,
  highlights: UserHighlight[],
): JSX.Element {
  const annotationIds = highlights.map(h => h.data.id);
  return (
    <AnnotationPopover annotationIds={annotationIds}>
      {RenderElement}
    </AnnotationPopover>
  );
}
